import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';

export interface SingleH1Options {
  /**
   * Custom check to determine if an h1 already exists in the document
   */
  hasH1: ((editor: Editor) => boolean) | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    singleH1: {
      /**
       * Check if an h1 already exists in the document
       */
      hasH1: () => ReturnType;
    };
  }
}

/**
 * Checks if the document contains an h1 heading
 */
export function documentHasH1(editor: Editor): boolean {
  let hasH1 = false;

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'heading' && node.attrs['level'] === 1) {
      hasH1 = true;
      return false; // Stop traversing
    }
    return true;
  });

  return hasH1;
}

/**
 * Extension that enforces a single h1 rule in the document.
 * This extension provides a command to check if an h1 exists,
 * which can be used by other extensions to conditionally allow h1 creation.
 */
export const SingleH1Extension = Extension.create<SingleH1Options>({
  name: 'singleH1',

  addOptions() {
    return {
      hasH1: null,
    };
  },

  addCommands() {
    return {
      hasH1:
        () =>
        ({ editor }) => {
          const checkFn = this.options.hasH1 ?? documentHasH1;
          return checkFn(editor);
        },
    };
  },

  addStorage() {
    return {
      /**
       * Helper to check if h1 is available (no h1 exists yet)
       */
      canCreateH1: (editor: Editor): boolean => {
        const checkFn = this.options.hasH1 ?? documentHasH1;
        return !checkFn(editor);
      },
    };
  },
});
